/**
 * Neutron Star Visualization - Application Entry Point
 *
 * Bootstraps the 3D scene with a spinning neutron star,
 * jet streams, and interactive control panel.
 */

import * as THREE from 'three';
import { SceneManager } from './SceneManager';
import { ControlPanel } from './ControlPanel';
import { MobileControlPanel } from './MobileControlPanel';
import { MobileDetector } from './MobileDetector';
import { PerformanceScaler } from './PerformanceScaler';
import { VisualizationParams } from './types';

function main(): void {
  const container = document.getElementById('app');
  if (!container) {
    throw new Error('Could not find #app container element');
  }

  // Detect mobile device
  const mobileDetector = new MobileDetector();

  // Get performance configuration based on device type
  const performanceConfig = PerformanceScaler.getConfig(mobileDetector.isMobile);

  // Instantiate SceneManager with the container element
  const sceneManager = new SceneManager({
    container,
    isMobile: mobileDetector.isMobile,
    performanceConfig,
  });

  // Instantiate ControlPanel with onChange callback that dispatches to SceneManager components
  const applyParams = (params: VisualizationParams) => {
    sceneManager.getNeutronStar().setSpinSpeed(params.spinSpeed);
    sceneManager.getNeutronStar().setInclinationAngle(params.inclinationAngle);
    sceneManager.getNeutronStar().setColor(new THREE.Color(params.starColor));
    sceneManager.getJetSystem().setMagneticOffset(params.magneticOffsetAngle);
    sceneManager.getJetSystem().setSpeed(params.jetSpeed);
    sceneManager.getJetSystem().setParticleCount(
      params.jetParticleCount,
      mobileDetector.isMobile ? performanceConfig.maxParticleCount : undefined
    );
    sceneManager.getJetSystem().setAccentColor(new THREE.Color(params.jetAccentColor));
    sceneManager.updateOrbitalParams({
      orbitalSpeed: params.orbitalSpeed,
      eccentricity: params.eccentricity,
      semiMajorAxis: params.semiMajorAxis,
    });
    sceneManager.updateFieldLineParams({
      fieldLineCount: params.fieldLineCount,
      azimuthalPlaneCount: params.azimuthalPlaneCount,
      fieldLineExtent: params.fieldLineExtent,
      fieldLineColor: params.fieldLineColor,
      visible: params.fieldLineVisible,
    });
  };

  const controlPanel = new ControlPanel(applyParams);

  // Wrap control panel with mobile UX when on mobile
  if (mobileDetector.isMobile) {
    new MobileControlPanel(controlPanel);
  }

  // Clamp initial jet particle count to mobile maximum
  if (mobileDetector.isMobile) {
    controlPanel.params.jetParticleCount = Math.min(
      controlPanel.params.jetParticleCount,
      performanceConfig.maxParticleCount
    );
  }

  // Apply initial params so defaults match the control panel
  applyParams(controlPanel.params);

  // Start the animation loop
  sceneManager.start();
}

document.addEventListener('DOMContentLoaded', main);
